/** Camera windows placed in the session's room, with optional controller dragging. */
import {
  BufferGeometry, Group, Line, LineBasicMaterial, Matrix4, Mesh,
  Quaternion, Raycaster, Vector3,
} from "three";

interface WindowHandle { slot: number; group: Group; handle: Mesh; highlight: (on: boolean) => void; }
interface Placement { p: number[]; q: number[]; s?: number; }
interface Drag { window: WindowHandle; distance: number; offset: Vector3; }

export const XR_WINDOW_MIN_SCALE = .4;
export const XR_WINDOW_MAX_SCALE = 3;
const placementScale = (placement: Placement) => typeof placement.s === "number" && Number.isFinite(placement.s)
  ? Math.min(XR_WINDOW_MAX_SCALE, Math.max(XR_WINDOW_MIN_SCALE, placement.s)) : 1;

export function createWindowLayout(root: Group, getWindows: () => WindowHandle[],
  { editable = false, storageKey }: { editable?: boolean; storageKey?: string } = {}) {
  const saved: Record<string, Placement> = {};
  try {
    const data = storageKey && JSON.parse(localStorage.getItem(storageKey) ?? "null");
    if (data?.version === 1 && data.windows && typeof data.windows === "object") {
      for (const [slot, value] of Object.entries(data.windows)) {
        const placement = value as Placement;
        if (placement?.p?.length === 3 && placement?.q?.length === 4
          && [...placement.p, ...placement.q].every(Number.isFinite)
          && new Vector3().fromArray(placement.p).length() < 6
          && new Quaternion().fromArray(placement.q).length() > .1) {
          saved[slot] = { ...placement, s: placementScale(placement) };
        }
      }
    }
  } catch { /* Storage is optional in a private headset browser. */ }
  const defaults = new Map<Group, Placement>();
  const boundSlots = new Map<Group, number>();
  const pressed = new Map<XRInputSource, boolean>();
  const drags = new Map<XRInputSource, Drag>();
  const pointers = new Map<XRInputSource, Line>();
  const pointerGroup = new Group();
  root.add(pointerGroup);
  const raycaster = new Raycaster();
  const inverse = new Matrix4();
  let lastUpdateAt: number | null = null;

  const save = () => {
    for (const window of getWindows()) {
      if (window.slot >= 0) saved[window.slot] = {
        p: window.group.position.toArray(), q: window.group.quaternion.toArray(),
        s: window.group.scale.x,
      };
    }
    try {
      if (storageKey) localStorage.setItem(storageKey, JSON.stringify({ version: 1, windows: saved }));
    } catch { /* Keep the current arrangement even if persistence is unavailable. */ }
  };
  const removePointer = (source: XRInputSource) => {
    const line = pointers.get(source);
    if (!line) return;
    line.geometry.dispose();
    (line.material as LineBasicMaterial).dispose();
    line.removeFromParent();
    pointers.delete(source);
  };
  const release = (source: XRInputSource) => {
    if (drags.delete(source)) save();
  };

  return {
    sync() {
      for (const window of getWindows()) {
        if (!defaults.has(window.group)) defaults.set(window.group, {
          p: window.group.position.toArray(), q: window.group.quaternion.toArray(),
          s: window.group.scale.x,
        });
        if (window.slot < 0 || boundSlots.get(window.group) === window.slot) continue;
        boundSlots.set(window.group, window.slot);
        const placement = saved[window.slot] ?? defaults.get(window.group)!;
        window.group.position.fromArray(placement.p);
        window.group.quaternion.fromArray(placement.q).normalize();
        window.group.scale.setScalar(placementScale(placement));
      }
    },
    update(now: number, frame: XRFrame | undefined, refSpace: XRReferenceSpace | undefined,
      viewerPose: XRViewerPose | undefined) {
      const dt = lastUpdateAt == null ? 0 : Math.min(.1, Math.max(0, (now - lastUpdateAt) / 1000));
      lastUpdateAt = now;
      const windows = getWindows();
      windows.forEach((window) => window.highlight(false));
      const sources = editable && frame && refSpace && viewerPose
        ? Array.from(frame.session.inputSources).filter((source) =>
          !source.hand && source.targetRayMode === "tracked-pointer" && source.gamepad) : [];
      for (const source of [...pressed.keys()]) {
        if (sources.includes(source)) continue;
        release(source); pressed.delete(source); removePointer(source);
      }
      if (!frame || !refSpace || !viewerPose || !editable) return;
      root.updateWorldMatrix(true, true);
      inverse.copy(root.matrixWorld).invert();
      for (const source of sources) {
        const pose = frame.getPose(source.targetRaySpace, refSpace);
        if (!pose) {
          release(source); pressed.delete(source); removePointer(source); continue;
        }
        const { position: p, orientation: q } = pose.transform;
        const origin = new Vector3(p.x, p.y, p.z);
        const direction = new Vector3(0, 0, -1).applyQuaternion(new Quaternion(q.x, q.y, q.z, q.w));
        raycaster.set(origin, direction);
        const hit = raycaster.intersectObjects(windows.map((window) => window.handle), false)[0];
        const hovered = hit && windows.find((window) => window.handle === hit.object);
        const down = Boolean(source.gamepad?.buttons[0]?.pressed);
        const wasDown = pressed.get(source);
        // A trigger already held on entry/lost tracking must be released first.
        if (down && wasDown === false && hovered
          && ![...drags.values()].some((drag) => drag.window.group === hovered.group)) {
          const localHit = root.worldToLocal(hit.point.clone());
          drags.set(source, { window: hovered, distance: hit.distance,
            offset: hovered.group.position.clone().sub(localHit) });
        }
        if (!down) release(source);
        pressed.set(source, down);
        const drag = drags.get(source);
        let distance = hit?.distance ?? 3;
        if (drag) {
          const axis = source.gamepad?.axes[3] ?? 0;
          if (Math.abs(axis) > .15) drag.distance = Math.min(4, Math.max(.45, drag.distance + axis * dt));
          const resize = source.gamepad?.axes[2] ?? 0;
          if (Math.abs(resize) > .15) {
            const previousScale = drag.window.group.scale.x;
            const scale = Math.min(XR_WINDOW_MAX_SCALE, Math.max(XR_WINDOW_MIN_SCALE,
              previousScale * Math.exp(resize * dt)));
            drag.window.group.scale.setScalar(scale);
            // Keep the grabbed point on the header under the controller ray.
            drag.offset.multiplyScalar(scale / previousScale);
          }
          const localRay = raycaster.ray.clone().applyMatrix4(inverse);
          drag.window.group.position.copy(localRay.at(drag.distance, new Vector3())).add(drag.offset);
          drag.window.group.lookAt(viewerPose.transform.position.x,
            viewerPose.transform.position.y, viewerPose.transform.position.z);
          drag.window.highlight(true);
          distance = drag.distance;
        } else hovered?.highlight(true);
        let pointer = pointers.get(source);
        if (!pointer) {
          pointer = new Line(new BufferGeometry(), new LineBasicMaterial({ color: 0x7de5db,
            transparent: true, opacity: .75, depthTest: false }));
          pointer.renderOrder = 3;
          pointers.set(source, pointer); pointerGroup.add(pointer);
        }
        const end = origin.clone().addScaledVector(direction, distance);
        pointer.geometry.setFromPoints([root.worldToLocal(origin), root.worldToLocal(end)]);
      }
    },
    dispose() {
      if (drags.size) save();
      for (const source of [...pointers.keys()]) removePointer(source);
      drags.clear(); pressed.clear(); pointerGroup.removeFromParent();
    },
  };
}
